import {
  LegalAddress,
  LegalDocument,
  LegalItem,
  LegalLink,
  LegalList,
  LegalSection,
  LegalStrong,
  LegalText,
} from '../ui/LegalDocument';
import { TERMS_VERSION } from '../lib/legalDocuments';

/**
 * The terms of service — a real document, reachable signed out, at `/terms` (TROOP-203).
 *
 * <b>The clause this document exists for is the youth-protection one</b>, and it is first for that
 * reason rather than because it reads well there. The product enforces a unit's safeguarding rules
 * in software — two adults in a thread, no covert reading, a guardian-gated sign-in — and the
 * hazard of doing that well is that a unit reads the tooling as the compliance. It is not: the unit
 * runs the program, trains and screens its own adults, and answers to its own chartering
 * organisation. Allocating that duty explicitly is the most consequential sentence on the page, so
 * it is said in the words a committee chair uses rather than in the words a contract usually would.
 *
 * <b>It must agree with the privacy policy, and neither may be edited alone.</b> The two are one
 * set of facts split across two documents — what is held, and who is answerable for it — and when
 * TROOP-152 lands the app-store privacy labels become a third copy of the same facts.
 *
 * <b>Nothing is charged today and the document says so.</b> TROOP-202 has not decided pricing and
 * TROOP-205 has not formed an entity, so this names no company, quotes no price and describes no
 * subscription. A liability cap phrased as "what you paid us" needs a floor for exactly that
 * reason, which is what the hundred dollars is doing.
 *
 * <b>What is deliberately a promise rather than a feature.</b> A unit can ask for a copy of its
 * data and get one; today that is a person exporting it by hand, because TROOP-211 is not built.
 * That is a commitment a human keeps, so it is safe to make here — unlike a self-serve export,
 * which would be a description of a button that does not exist.
 */
export function TermsScreen() {
  return (
    <LegalDocument title="Terms of service" version={TERMS_VERSION}>
      <LegalText>
        These terms are the agreement between TroopHerald and the unit using it, and between
        TroopHerald and each person who signs in. They are written to be read rather than to be
        got past, so they are shorter and plainer than they could be. If anything here is unclear,
        write to <LegalAddress /> and ask.
      </LegalText>
      <LegalText>
        TroopHerald is operated by a sole proprietor based in Connecticut, in the United States.
        What is held about you and your family, and who can see it, is the{' '}
        <LegalLink href="/privacy">privacy policy</LegalLink>.
      </LegalText>

      <LegalSection title="Youth protection is the unit’s responsibility">
        <LegalText>
          This is the most important clause in this document and it is not buried.{' '}
          <LegalStrong>
            The unit runs its program and is responsible for its own youth protection.
          </LegalStrong>{' '}
          That includes screening and training the adults it gives access to, supervising its own
          activities, following the rules of its chartering organisation and its national program,
          and meeting whatever the law where it operates requires of it.
        </LegalText>
        <LegalText>
          TroopHerald provides tooling that supports those rules. A conversation with young people
          in it is held to two adults; nobody can read a thread without the people in it being
          told; a child cannot sign in until a guardian has set their access up. Those are real and
          they are enforced by the product.{' '}
          <LegalStrong>They are not a substitute for any of the unit&rsquo;s obligations</LegalStrong>{' '}
          and they do not transfer any of them to us. We do not screen, vet, train or supervise
          anybody. The unit decides who its adults are, who leads, who joins, and who is given
          which permissions — and a rule enforced in software is only ever as good as the roster
          the unit keeps.
        </LegalText>
        <LegalText>
          If something happens that a unit is required to report, the unit reports it. Nothing in
          this product does that for you, and nothing here should be understood as us having taken
          on that duty.
        </LegalText>
      </LegalSection>

      <LegalSection title="Who may use it">
        <LegalList>
          <LegalItem>
            An adult accepts these terms on behalf of their unit, and must have the
            unit&rsquo;s authority to do so.
          </LegalItem>
          <LegalItem>
            A young person may use TroopHerald only once a guardian has set their access up. That
            is a rule the product enforces, not only a term in this document.
          </LegalItem>
          <LegalItem>
            Accounts belong to one person. Do not share a sign-in, and do not sign in as somebody
            else. A guardian holds their child&rsquo;s sign-up link and the mailbox it is bound to,
            so the product cannot stop a parent signing in as their child — it is asked of them
            here instead, because a child&rsquo;s own account is how the unit knows who said what.
          </LegalItem>
        </LegalList>
      </LegalSection>

      <LegalSection title="What a unit may expect of us">
        <LegalText>
          TroopHerald is in beta and is offered by invitation. It is free while that is true. If it
          stops being free, a unit will be told what it costs and asked, before anything is
          charged — nothing here starts a payment on its own.
        </LegalText>
        <LegalText>
          We will keep the product running as well as we reasonably can and tell unit
          administrators about anything material: a change to these terms, a change to the privacy
          policy, or an incident affecting their data.
        </LegalText>
      </LegalSection>

      <LegalSection title="The unit’s data belongs to the unit">
        <LegalText>
          What a unit puts into TroopHerald is the unit&rsquo;s. We claim no ownership of it. We
          hold a narrow permission to store it, process it and show it to the people the unit has
          given access to — that is, to run the product for you, and for nothing else.
        </LegalText>
        <LegalList>
          <LegalItem>
            A unit can ask for a copy of everything it has put in, at any time, and get one. Today
            that is an email to <LegalAddress /> and somebody exporting it by hand.
          </LegalItem>
          <LegalItem>
            We do not sell it, rent it, or share it with anybody outside the companies listed in
            the <LegalLink href="/privacy">privacy policy</LegalLink> that are needed to run the product.
          </LegalItem>
          <LegalItem>
            We do not use what a unit writes, uploads or photographs to train anything, ours or
            anybody else&rsquo;s.
          </LegalItem>
        </LegalList>
      </LegalSection>

      <LegalSection title="Using it properly">
        <LegalText>Do not use TroopHerald to:</LegalText>
        <LegalList>
          <LegalItem>
            harm, harass, exploit or endanger a young person, or contact one outside the
            unit&rsquo;s own rules for how adults and young people communicate;
          </LegalItem>
          <LegalItem>
            post something you do not have the right to post — a photograph of somebody else&rsquo;s
            child included, where the unit&rsquo;s own arrangements do not cover it;
          </LegalItem>
          <LegalItem>
            reach data belonging to another unit, or to a family whose things you have not been
            given, or to test whether you can;
          </LegalItem>
          <LegalItem>
            collect data out of it automatically, or run it through anything other than the app and
            the calendar subscription we provide;
          </LegalItem>
          <LegalItem>break the law, or help somebody else break it.</LegalItem>
        </LegalList>
      </LegalSection>

      <LegalSection title="Reports, and our right to act">
        <LegalText>
          Anybody in a unit can report a message. A report is private from the person it is about
          and visible to whoever raised it, and an adult in the unit closes it. That is how it
          works inside a unit, and it is deliberately not a route to us.
        </LegalText>
        <LegalText>
          Separately, we may act ourselves where something reaches us that warrants it: removing
          content, suspending an account, or ending a unit&rsquo;s access. Where a child appears to
          be at risk we may contact the unit&rsquo;s leadership and, where the law requires or
          permits it, the appropriate authorities. We will not do any of this quietly where telling
          you is safe and lawful.
        </LegalText>
      </LegalSection>

      <LegalSection title="What this is not">
        <LegalText>
          <LegalStrong>Not a system of record.</LegalStrong> If your unit is required to keep a
          signed permission slip, a medical form or an attendance record, keep your own copy. A
          scan uploaded here is a convenience for the people running the trip, not your archive.
        </LegalText>
        <LegalText>
          <LegalStrong>Not an emergency system.</LegalStrong> Do not rely on it to reach somebody
          urgently. A message may be delayed, an email may not arrive, and a phone may be in a
          rucksack.
        </LegalText>
        <LegalText>
          <LegalStrong>Provided as it is.</LegalStrong> We give no warranty that TroopHerald will be
          available, uninterrupted, or free of faults, and no warranty of fitness for a particular
          purpose, to the extent the law allows us to say so. It is a young product in beta and
          some of it will be wrong.
        </LegalText>
      </LegalSection>

      <LegalSection title="Liability">
        <LegalText>
          To the extent the law allows, TroopHerald is not liable for indirect or consequential
          loss, for lost data where you could reasonably have kept your own copy, or for anything
          arising from how a unit runs its own program. Our total liability for any claim is limited
          to the greater of what your unit has paid us in the twelve months before it arose, or one
          hundred United States dollars.
        </LegalText>
        <LegalText>
          Nothing here limits liability for anything that cannot lawfully be limited, including
          death or personal injury caused by negligence, or fraud.
        </LegalText>
      </LegalSection>

      <LegalSection title="Ending it">
        <LegalText>
          A unit can stop using TroopHerald whenever it likes, and so can any person in it. We can
          end a unit&rsquo;s access if these terms are broken, if the product is being used in a way
          that puts a young person at risk, or if we stop offering the product — with reasonable
          notice in the last case.
        </LegalText>
        <LegalText>
          When a unit&rsquo;s access ends, it has sixty days to ask for a copy of its data. After
          that the unit&rsquo;s content is removed from the live product. Backups age out on their
          own schedule rather than instantly, which is an honest limit of how backups work, and what
          is destroyed rather than hidden is set out in the{' '}
          <LegalLink href="/privacy">privacy policy</LegalLink>.
        </LegalText>
      </LegalSection>

      <LegalSection title="Changes, and the law that applies">
        <LegalText>
          The version at the top of this page changes when these terms change, so which version
          applied at a given moment can always be identified. Unit administrators are told by email
          about a material change before it takes effect.
        </LegalText>
        <LegalText>
          These terms are governed by the laws of the State of Connecticut, United States. Questions
          about any of it go to <LegalAddress />.
        </LegalText>
      </LegalSection>
    </LegalDocument>
  );
}

